import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/store/types';
import { LICENSE_KSA } from '~/utils/license-utils';
import useContextualPathTransformer from '~/hooks/use-contextual-path-transformer';
import { LIMITS_SCREEN } from '~/components/account/limits/responsible-gaming/constants';
import { ROUTE_ACCOUNT_MY_LIMITS } from '~/components/router/constants';
import { selectLicense } from '../../../brand/ducks/selectors';
import { checkDepositLimitsHalfExceeded, LimitInfo } from './api';
import { useTypedTranslationsWithPartialResult } from '~/components/translation/use-translations';
import { showQueuedNotification } from '~/components/notifications/ducks/actions';
import useConfiguration from '../../../../configuration/use-configuration';

enum TextKeys {
	MOBILE_DAILY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION = 'MOBILE_DAILY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION',
	MOBILE_WEEKLY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION = 'MOBILE_WEEKLY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION',
	MOBILE_MONTHLY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION = 'MOBILE_MONTHLY_PLAYED_DEPOSIT_LIMIT_NOTIFICATION',
	MOBILE_DEPOSIT_LIMITS_SETUP_BUTTON = 'MOBILE_DEPOSIT_LIMITS_SETUP_BUTTON'
}

const DEFAULT_DURATION = 7; // 7 seconds

const useDepositLimitCheck = () => {
	const [halfLimitsExceeded, setHalfLimitsExceeded] = useState<LimitInfo[]>([]);
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const { isLoadingTranslations, translations } = useTypedTranslationsWithPartialResult(TextKeys);
	const transformPath = useContextualPathTransformer();
	const license = useSelector(selectLicense);
	// TODO: possibly a feature toggle later on?
	const checkLimitsAfterDeposit = license === LICENSE_KSA;
	const configuration = useConfiguration();
	const notificationDuration = configuration?.depositLimitNotificationDuration ?? DEFAULT_DURATION;
	// we maintain this, because the consumers of the hook may end up calling the function many times for same transaction
	const transactionRef = useRef<string>('');
	const previousLimitsRef = useRef<LimitInfo[]>([]);

	// Effect to wait for translations to finish loading and to handle showing notifications when limits are exceeded, only when the state changes
	useEffect(() => {
		if (
			!isLoadingTranslations &&
			halfLimitsExceeded?.length > 0 &&
			JSON.stringify(previousLimitsRef.current) !== JSON.stringify(halfLimitsExceeded)
		) {
			previousLimitsRef.current = halfLimitsExceeded; // Update the previous state

			halfLimitsExceeded.forEach((value: LimitInfo) => {
				dispatch(
					showQueuedNotification({
						message: translations[
							`MOBILE_${value.type}_PLAYED_DEPOSIT_LIMIT_NOTIFICATION` as TextKeys
						].replace('{limitValue}', String(value.limit)),
						autoHide: true,
						autoHideDuration: notificationDuration * 1000,
						color: 'neutral',
						buttonText: translations.MOBILE_DEPOSIT_LIMITS_SETUP_BUTTON,
						buttonBottom: true,
						buttonCB: () => {
							navigate(transformPath(`${ROUTE_ACCOUNT_MY_LIMITS}/${LIMITS_SCREEN.DEPOSIT}`));
						}
					})
				);
			});
		}
	}, [
		halfLimitsExceeded,
		translations,
		navigate,
		transformPath,
		dispatch,
		notificationDuration,
		isLoadingTranslations
	]);

	const handleDepositLimitNotification = async (accHistRef?: string): Promise<void> => {
		// only run this for relevant markets and if accHist has changed, was triggered multiple times
		if (!checkLimitsAfterDeposit || transactionRef.current === accHistRef) return;

		try {
			const response = await checkDepositLimitsHalfExceeded();

			setHalfLimitsExceeded(response);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error('Failed to check deposit limits: ', error);
		}

		transactionRef.current = accHistRef ?? '';
	};

	return { handleDepositLimitNotification };
};

export default useDepositLimitCheck;
